import { springs } from "@/components/ui/motion";
import { cn } from "@/packages/utils";
import { motion } from "framer-motion";
import type { ReactNode } from "react";

/** Shared cap for overview row-2 cards (details + AI analysis). */
export const overviewTallCardClassName = "max-h-[360px]";

const sizeStyles = {
  S: "col-span-1 h-[200px]",
  M: "col-span-1 md:col-span-2 h-[200px]",
  L: "col-span-full h-[280px]",
  auto: "h-full",
} as const;

export type WidgetVariant = "elevated" | "accent" | "standard";

const variantStyles: Record<WidgetVariant, string> = {
  elevated:
    "border-t-2 border-t-accent/40 bg-gradient-to-b from-white to-[#fafaf8] card-elevated-glow",
  accent: "bg-[#5a8a720a] border-l-2 border-l-accent/30",
  standard: "bg-card",
};

const variantInitial: Record<WidgetVariant, Record<string, number>> = {
  elevated: { opacity: 0, y: 20, scale: 0.98 },
  accent: { opacity: 0, x: -12 },
  standard: { opacity: 0, y: 12 },
};

const variantAnimate: Record<WidgetVariant, Record<string, number>> = {
  elevated: { opacity: 1, y: 0, scale: 1 },
  accent: { opacity: 1, x: 0 },
  standard: { opacity: 1, y: 0 },
};

export interface WidgetCardProps {
  size: "S" | "M" | "L" | "auto";
  title?: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
  isTerminal?: boolean;
  className?: string;
  index?: number;
  variant?: WidgetVariant;
}

export function WidgetCard({
  size,
  title,
  icon,
  action,
  children,
  expandable: _expandable,
  expanded,
  onToggle: _onToggle,
  onClick,
  isTerminal,
  className,
  index,
  variant = "standard",
}: WidgetCardProps) {
  return (
    <motion.div
      className={cn(
        sizeStyles[size],
        "rounded-xl border border-border p-3 flex flex-col overflow-hidden",
        variantStyles[variant],
        !isTerminal &&
          "hover:shadow-md hover:shadow-black/[0.06] hover:-translate-y-0.5 transition-all duration-200",
        onClick && !isTerminal && "cursor-pointer active:scale-[0.995]",
        isTerminal && "opacity-90",
        className,
      )}
      initial={variantInitial[variant]}
      animate={{
        ...variantAnimate[variant],
        height:
          expanded !== undefined
            ? expanded
              ? size === "auto"
                ? "auto"
                : 360
              : size === "auto"
                ? "auto"
                : 200
            : undefined,
      }}
      transition={{ ...springs.smooth, delay: (index ?? 0) * 0.04 }}
      layout={expanded !== undefined}
      onClick={onClick}
    >
      {title && (
        <div className="flex items-center justify-between mb-2">
          <span
            className={cn(
              "text-primary",
              variant === "elevated"
                ? "text-base font-bold"
                : "text-sm font-semibold",
            )}
          >
            {title}
          </span>
          <div className="flex items-center gap-2">
            {action}
            {icon && <div className="text-muted">{icon}</div>}
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0">{children}</div>
    </motion.div>
  );
}

export function WidgetCardSkeleton({
  size,
  className,
  index,
}: {
  size: "S" | "M" | "L" | "auto";
  className?: string;
  index?: number;
}) {
  return (
    <motion.div
      className={cn(
        sizeStyles[size],
        "rounded-xl bg-card border border-border p-5 flex flex-col overflow-hidden animate-pulse",
        className,
      )}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.smooth, delay: (index ?? 0) * 0.04 }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="h-4 w-24 bg-border/50 rounded" />
        <div className="h-4 w-4 bg-border/50 rounded" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-6 w-16 bg-border/50 rounded" />
        <div className="h-2 w-full bg-border/50 rounded" />
      </div>
    </motion.div>
  );
}
